/**
 * The outgoing view of a transition (Option C C1b, review C1B-03): a static copy that keeps the view the user is
 * leaving on screen while it fades out, overlapping the incoming view's fade — so no frame of a view change is empty
 * (the prototype's `start` / `pane` fade-outs). React unmounts the real view at commit; the copy is taken just before.
 *
 * The copy is decoration only: `aria-hidden`, `inert`, `pointer-events: none`, and stripped of everything that could
 * make it count as the real view — ids (HTML only: an icon's SVG gradient ids stay, they are shared already), names,
 * `form` / `for` links, test ids, focus roles, the layout's `data-dialog-fixed` parts, every `data-flip-*` hook (a
 * flip item's key survives as `data-outgoing-key`, so the items that fly are hidden in the copy and never doubled), and
 * every label: `aria-label`, `title`, `alt` and the other naming attributes go, and each `<label>` becomes a `<span>`,
 * so a label query (`getByLabel('Show password')`) finds only the real control (review C1B-06). Placeholders stay: they
 * are what an empty field shows, and no label query reads them. Nothing in it can be found, focused, clicked or measured
 * as the dialog.
 *
 * What the copy cannot inherit it is given: form values (a select's choice; inputs copy their value when cloned),
 * scroll positions, and the animated opacity / transform / size of any element that was mid-flight, so a view left
 * during a flight fades out from where it visibly was.
 */
import { frameScale, localBox, px, type Box } from './flipMath';

export const OUTGOING_ATTRIBUTE = 'data-flip-outgoing';

export type OutgoingView = {
  copy: HTMLElement;
  box: Box;
  /** The view's own opacity when it was copied (a page caught mid-fade). */
  opacity: number;
  scroll: ReadonlyArray<{ index: number; top: number; left: number }>;
};

const STRIPPED = [
  'name', 'form', 'for', 'data-testid', 'data-focus-role', 'data-dialog-fixed', 'data-rail-stop', 'autofocus', 'tabindex',
  'aria-controls', 'aria-activedescendant', 'aria-labelledby', 'aria-describedby', 'aria-details', 'aria-errormessage',
  'aria-label', 'aria-description', 'aria-roledescription', 'aria-valuetext', 'aria-placeholder', 'title', 'alt',
];

/** A `<label>` names the control inside it even without `for`: in the copy it becomes a `<span>` that looks the same. */
function unlabel(copy: HTMLElement): void {
  copy.querySelectorAll('label').forEach((label) => {
    const span = document.createElement('span');
    span.className = label.className;
    span.style.cssText = label.style.cssText;
    while (label.firstChild) span.appendChild(label.firstChild);
    label.replaceWith(span);
  });
}

function strip(element: Element): void {
  if (!(element instanceof SVGElement)) element.removeAttribute('id');
  for (const name of STRIPPED) element.removeAttribute(name);
  for (const { name } of Array.from(element.attributes)) {
    if (!name.startsWith('data-flip-')) continue;
    if (name === 'data-flip-key') element.setAttribute('data-outgoing-key', element.getAttribute(name) ?? '');
    element.removeAttribute(name);
  }
}

/** Every element of `root` and its subtree, in document order (a copy made with `cloneNode(true)` has the same order). */
const subtree = (root: Element): Element[] => [root, ...Array.from(root.querySelectorAll('*'))];

function animatedTargets(element: HTMLElement): Set<Element> {
  const animations = typeof element.getAnimations === 'function' ? element.getAnimations({ subtree: true }) : [];
  return new Set(animations.map((animation) => (animation.effect as KeyframeEffect | null)?.target).filter((target): target is Element => !!target));
}

/** Copies `element` for the fade-out (before the commit that removes it). */
export function captureOutgoing(root: HTMLElement, element: HTMLElement): OutgoingView | null {
  const rootRect = root.getBoundingClientRect();
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  const box = localBox(rect, rootRect, frameScale(rootRect.width, root.offsetWidth));
  const copy = element.cloneNode(true) as HTMLElement;
  const originals = subtree(element);
  const copies = subtree(copy);
  const animated = animatedTargets(element);
  const scroll: Array<{ index: number; top: number; left: number }> = [];
  originals.forEach((original, index) => {
    const twin = copies[index] as HTMLElement | SVGElement | undefined;
    if (!twin) return;
    if (original instanceof HTMLSelectElement && twin instanceof HTMLSelectElement) twin.selectedIndex = original.selectedIndex;
    if (original.scrollTop || original.scrollLeft) scroll.push({ index, top: original.scrollTop, left: original.scrollLeft });
    if (animated.has(original) && index > 0) {
      const style = getComputedStyle(original);
      twin.style.opacity = style.opacity;
      twin.style.transform = style.transform;
      twin.style.transformOrigin = style.transformOrigin;
      twin.style.filter = style.filter;
      if (original.getAttribute('data-flip-layer') === 'surface') {
        twin.style.width = style.width;
        twin.style.height = style.height;
      }
    }
    strip(twin);
  });
  // After the walk (it pairs originals and copies by position): one element for one, so the order still matches.
  unlabel(copy);
  const opacity = Number.parseFloat(getComputedStyle(element).opacity);
  return { copy, box, opacity: Number.isFinite(opacity) ? opacity : 1, scroll };
}

/**
 * Puts the copy back where the view was, under everything the incoming view positions (it is the dialog's first child),
 * with the items that fly (`flyingKeys`) hidden — they are on screen as the incoming view's layers.
 */
export function insertOutgoing(root: HTMLElement, view: OutgoingView, flyingKeys: ReadonlySet<string>): HTMLElement {
  const { copy, box } = view;
  copy.setAttribute(OUTGOING_ATTRIBUTE, '');
  copy.setAttribute('aria-hidden', 'true');
  copy.setAttribute('inert', '');
  Object.assign(copy.style, {
    position: 'absolute',
    left: px(box.x - root.clientLeft),
    top: px(box.y - root.clientTop),
    width: px(box.w),
    height: px(box.h),
    margin: '0',
    pointerEvents: 'none',
    opacity: String(view.opacity),
    transform: 'none',
    filter: 'none',
  });
  copy.querySelectorAll<HTMLElement>('[data-outgoing-key]').forEach((item) => {
    if (flyingKeys.has(item.getAttribute('data-outgoing-key') ?? '')) item.style.visibility = 'hidden';
  });
  root.insertBefore(copy, root.firstChild);
  const copies = subtree(copy);
  for (const { index, top, left } of view.scroll) {
    const twin = copies[index];
    if (twin) {
      twin.scrollTop = top;
      twin.scrollLeft = left;
    }
  }
  return copy;
}

export function removeOutgoing(root: HTMLElement): void {
  root.querySelectorAll(`[${OUTGOING_ATTRIBUTE}]`).forEach((copy) => copy.remove());
}
